/**
 * Audio Player Utility
 * Plays base64 encoded audio streams without saving files
 */

/**
 * Play base64 encoded audio
 * @param base64Audio - Base64 encoded audio string (MP3)
 * @returns Promise that resolves when audio finishes playing
 */
export const playBase64Audio = async (base64Audio: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      // Convert base64 to blob
      const binaryString = atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'audio/mpeg' });

      // Create object URL from blob
      const audioUrl = URL.createObjectURL(blob);

      // Create and play audio element
      const audio = new Audio(audioUrl);

      audio.onended = () => {
        // Clean up object URL when audio finishes
        URL.revokeObjectURL(audioUrl);
        console.log('🎵 Audio playback completed');
        resolve();
      };

      audio.onerror = (error) => {
        URL.revokeObjectURL(audioUrl);
        console.error('❌ Audio playback error:', error);
        reject(new Error('Failed to play audio'));
      };

      console.log('🎵 Playing AI customer audio...');
      audio.play().catch(reject);
    } catch (error) {
      console.error('❌ Error creating audio from base64:', error);
      reject(error);
    }
  });
};

/**
 * Stop currently playing audio (if any)
 * Note: This is a simple implementation. For more control, you'd need to track the audio element.
 */
export const stopAudio = () => {
  // Get all audio elements and pause them
  const audioElements = document.querySelectorAll('audio');
  audioElements.forEach(audio => {
    audio.pause();
    audio.currentTime = 0;
  });
  console.log('🔇 Stopped all audio playback');
};
